import { Box } from '@mui/material';
import { useState } from 'react';
import {
  CTR_CLICKABLE_NOTE,
  compactCurrency,
  compactNum,
  currency,
  dayOfWeekLabel,
  num,
  percent,
  percentWhole,
} from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import BarChart from './BarChart';
import MetricToggle from './MetricToggle';
import WidgetCard from './WidgetCard';
import WidgetExportButton from './WidgetExportButton';

const KEY = 'dayOfWeek';

const METRIC_OPTIONS = [
  { key: 'impressions', label: 'Impressions' },
  { key: 'ctr', label: 'CTR', note: CTR_CLICKABLE_NOTE },
  { key: 'vcr', label: 'VCR' },
];

const formatValue =
  (metric: string) =>
  (value: unknown): string => {
    if (metric === 'spend') return currency(value);
    // VCR drops decimals (prep #0000 — "can it just be 85%?"); CTR keeps its own.
    if (metric === 'vcr') return percentWhole(value);
    if (metric === 'ctr') return percent(value);
    return num(value);
  };

/** Axis ticks in the compact form; the tooltip keeps full precision. */
const formatTick =
  (metric: string) =>
  (value: unknown): string => {
    if (metric === 'spend') return compactCurrency(value);
    if (metric === 'vcr') return percentWhole(value);
    if (metric === 'ctr') return percent(value);
    return compactNum(value);
  };

/** Vertical bar chart for performance by day of week, toggled by metric. */
const DayOfWeekCard = () => {
  const [metric, setMetric] = useState('impressions');
  const { rows, loading, error } = useReportWidget(KEY, {
    dimensions: ['day_of_week'],
    metrics: [metric],
    sort: [{ field: 'day_of_week', dir: 'asc' }],
    limit: 7,
  });

  const labels = rows.map(row => dayOfWeekLabel(row.day_of_week));
  const values = rows.map(row => Number(row[metric] ?? 0));
  const onExportCsv = useExportWidgetCsv('dayOfWeek', rows);

  return (
    // Title and toggle go through WidgetCard's own header rather than children:
    // children are replaced wholesale by the loading / error / empty branch, so
    // an empty range previously rendered an untitled box with no way to switch
    // metric -- exactly when you'd want to try another one.
    <WidgetCard
      title='Performance by Day of Week'
      headerAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <MetricToggle options={METRIC_OPTIONS} value={metric} onChange={setMetric} />
          <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
        </Box>
      }
      loading={loading}
      error={error}
      isEmpty={rows.length === 0}
    >
      <BarChart labels={labels} values={values} format={formatValue(metric)} tickFormat={formatTick(metric)} />
    </WidgetCard>
  );
};

export default DayOfWeekCard;
